import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import {
  ModalWrapper,
  Button,
  Loader,
  ModalGeneric,
  styled,
} from "@payright/web-components";
import { fetchStatementPdf } from "../actions/fetchStatementPdf";
import { generateStatement } from "../actions/generateStatement";

type StatementModalProps = {
  onClose: () => void;
  onNewStatementGenerated?: () => void;
  statementId?: string;
  fromDate: string;
  toDate: string;
};

type ModalContentProps = {
  isGenerating: boolean;
  isCompleted: boolean;
  isFailed: boolean;
  pdfUrl: string | null;
  error: string | null;
};

const StyledModalBody = styled.article<{ paddingBottom?: string }>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  width: 100%;
  max-width: 80rem;
  padding-bottom: ${(props) => props.paddingBottom};
`;

const StyledPdfViewer = styled.div`
  width: 100%;

  .pdf-object {
    width: 100%;
    height: 60vh;
    border: 1px solid ${(props) => props.theme.colours.grey.light};
    border-radius: 5px;
  }
`;

const MG = styled(ModalGeneric)`
  width: 64rem;
  max-width: calc(95vw - 3rem);
`;

const ModalContent = ({
  isGenerating,
  isCompleted,
  isFailed,
  pdfUrl,
  error,
}: ModalContentProps) => {
  if (isGenerating) {
    return (
      <StyledModalBody>
        <Loader text="Generating statement..." />
        <p>
          This may take a few minutes. The page will update automatically when
          ready.
        </p>
      </StyledModalBody>
    );
  }

  if (isFailed) {
    return (
      <StyledModalBody paddingBottom="1rem">
        <p style={{ color: "#dc3545" }}>
          ❌ {error || "Statement generation failed"}
        </p>
      </StyledModalBody>
    );
  }

  if (isCompleted && pdfUrl) {
    return (
      <StyledModalBody>
        <StyledPdfViewer>
          <object
            data={pdfUrl + "#toolbar=0&navpanes=0&scrollbar=0"}
            type="application/pdf"
            className="pdf-object"
            aria-label="Statement PDF"
          />
        </StyledPdfViewer>
      </StyledModalBody>
    );
  }

  return (
    <StyledModalBody>
      <Loader text="Loading statement..." />
    </StyledModalBody>
  );
};

const StatementModal: React.FC<StatementModalProps> = ({
  onClose,
  onNewStatementGenerated,
  statementId,
  fromDate,
  toDate,
}) => {
  const [generatedStatementId, setGeneratedStatementId] =
    useState<string | undefined>(statementId);
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [polling, setPolling] = useState(false);
  const pollingIntervalRef = useRef<number | undefined>(undefined);

  const stopPolling = () => {
    setPolling(false);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = undefined;
    }
  };

  useEffect(() => {
    if (!generatedStatementId) {
      generateStatement({
        fromDate,
        toDate,
      }).then(result => {
        if (result.success) {
          setGeneratedStatementId(result.statement.id);
          onNewStatementGenerated?.();
        } else {
          setError(result.message);
        }
      });
    } else {
      setPdfUrl(undefined);
      setError(undefined);
      stopPolling();

      setPolling(true);
      pollingIntervalRef.current = setInterval(async () => {
        const pdfBlob = await fetchStatementPdf(generatedStatementId);

        if (!pdfBlob) {
          return;
        }

        setPdfUrl(URL.createObjectURL(pdfBlob));
        stopPolling();
      }, 3000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't need onNewStatementGenerated
  }, [generatedStatementId, fromDate, toDate]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const handleExport = () => {
    if (pdfUrl) {
      const a = document.createElement("a");
      a.download = `PayrightCustomerStatement_${fromDate}_to_${toDate}.pdf`;
      a.href = pdfUrl;
      a.addEventListener("click", (e) => {
        setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
      });
      a.click();
    }
  };

  const isGenerating = polling && !pdfUrl && !error;
  const isCompleted = !!pdfUrl;
  const isFailed = !!error;

  return (
    <ModalWrapper open={true} handleClose={onClose}>
      <MG
        title={`Statement from ${moment(fromDate).format('DD/MM/YYYY')} to ${moment(toDate).format('DD/MM/YYYY')}`}
        content={
          <ModalContent
            isGenerating={isGenerating}
            isCompleted={isCompleted}
            isFailed={isFailed}
            pdfUrl={pdfUrl}
            error={error}
          />
        }
        primaryButton={
          isCompleted && pdfUrl ? (
            <Button colour="blue" handleClick={handleExport}>
              Export PDF
            </Button>
          ) : undefined
        }
        secondaryButton={
          <Button outline colour="blue" handleClick={onClose}>
            Close
          </Button>
        }
      />
    </ModalWrapper>
  );
};

export default StatementModal;
